// ─────────────────────────────────────────────────────────────────────────────
// IMPERSONATION SONRASI YÖNLENDİRME
//
// Süper admin bir kullanıcıya "geçtikten" sonra, o kullanıcı normal login olsaydı
// nereye düşecekse oraya yönlendirir. Login.tsx'teki rol/satış-ekranı mantığının
// sadeleştirilmiş ve yeniden kullanılabilir bir kopyasıdır (Login.tsx'e dokunmadan).
//
// Aynı alt servisleri kullanır:
//   - getScreenPermissionsByType (SystemUserScreenPermissions)
//   - salesService.getPykScreens / salesApi (QuickSaleScreen tipi)
// ─────────────────────────────────────────────────────────────────────────────
import { getScreenPermissionsByType } from '../migration/personnelDataService';
import salesService from '../salesService';
import { salesApi } from '../../config/apiClient';

export type NavigateFn = (path: string) => void;

export interface ImpersonationRedirectArgs {
  role?: string;
  companyId?: string;
  /** Kanonik kimlik = firebaseUid (SystemUserScreenPermissions bu uid üzerinden eşleşir). */
  uid: string;
}

async function getActiveQuickSaleScreenIds(uid: string): Promise<string[]> {
  try {
    const perms = await getScreenPermissionsByType(uid, 'quickSaleScreens');
    return perms.filter((r) => r.isEnabled && r.screenId).map((r) => String(r.screenId));
  } catch (e) {
    console.warn('[impersonate-redirect] quickSaleScreens yetki okuma hatası:', e);
    return [];
  }
}

async function getPermMap(uid: string, screenType: string): Promise<Record<string, any>> {
  const map: Record<string, any> = {};
  try {
    const rows = await getScreenPermissionsByType(uid, screenType);
    rows.forEach((r) => {
      if (r.isEnabled && r.screenId != null) map[String(r.screenId)] = r.settings ?? true;
    });
  } catch (e) {
    console.warn(`[impersonate-redirect] ${screenType} yetki okuma hatası:`, e);
  }
  return map;
}

async function navigateToQuickSaleScreen(firstScreenId: string, navigate: NavigateFn): Promise<void> {
  try {
    const screenResponse = await salesApi.get(`/api/QuickSaleScreen/${firstScreenId}`);
    const screenData = screenResponse.data;
    const screenType = (screenData.ScreenType || screenData.screenType || screenData.screentype || 'Standalone').toLowerCase();

    if (screenType === 'restaurant') {
      const restaurantScreenId =
        screenData.RestaurantScreenId ||
        screenData.restaurantScreenId ||
        screenData.RestaurantScreenID ||
        screenData.restaurantscreenid;
      if (restaurantScreenId) {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
      } else {
        navigate(`/hizli-satis/ekran/${firstScreenId}`);
      }
    } else if (screenType === 'package') {
      navigate(`/paket-ekrani/${firstScreenId}`);
    } else {
      navigate(`/hizli-satis/ekran/${firstScreenId}`);
    }
  } catch (apiError) {
    console.error('[impersonate-redirect] QuickSaleScreen tip kontrolü hatası:', apiError);
    navigate(`/hizli-satis/ekran/${firstScreenId}`);
  }
}

async function tryNavigateToSqlPykScreen(uid: string, navigate: NavigateFn): Promise<boolean> {
  try {
    const pykScreens = await salesService.getPykScreens();
    for (const screen of pykScreens || []) {
      if (!screen?.SettingsJson) continue;
      let settings: any;
      try {
        settings = JSON.parse(screen.SettingsJson);
      } catch {
        continue;
      }
      const assigned: any[] = settings?.assignedPersonnel || [];
      if (Array.isArray(assigned) && assigned.map(String).includes(String(uid))) {
        navigate(`/pyk-ekrani/${screen.Id}`);
        return true;
      }
    }
  } catch (err) {
    console.error('[impersonate-redirect] SQL PYK ekran kontrol hatası:', err);
  }
  return false;
}

/**
 * Impersonation sonrası kullanıcıyı rolüne (ve atanmış satış/üretim ekranlarına) göre
 * yönlendirir. Login.tsx'teki post-login yönlendirme davranışını taklit eder.
 */
export async function navigateAfterImpersonation(
  args: ImpersonationRedirectArgs,
  navigate: NavigateFn
): Promise<void> {
  const role = (args.role || '').toLowerCase();
  const { companyId, uid } = args;

  if (role === 'superadmin') {
    navigate('/superadmin');
    return;
  }
  if (role === 'admin') {
    navigate('/accounting/dashboard');
    return;
  }
  if (role === 'manager') {
    navigate('/manager');
    return;
  }

  // employee / courier (ve diğer işçi rolleri) — atanmış ekranlara göre.
  if (role === 'employee' || role === 'courier') {
    if (!companyId) {
      navigate('/personnel/tasks');
      return;
    }

    try {
      // 1) WMS ekranları
      const wmsPerms = await getScreenPermissionsByType(uid, 'wmsScreens');
      const activeWms = wmsPerms.filter((r) => r.isEnabled && r.screenId).map((r) => String(r.screenId));
      if (activeWms.length > 0) {
        navigate('/wms/secim');
        return;
      }

      // 2) Hızlı satış ekranları (restaurant/package/standalone tip ayrımıyla)
      const activeScreenIds = await getActiveQuickSaleScreenIds(uid);
      if (activeScreenIds.length > 0) {
        await navigateToQuickSaleScreen(activeScreenIds[0], navigate);
        return;
      }

      // 3) Mutfak ekranı
      const kitchen = await getPermMap(uid, 'kitchenScreens');
      for (const menuId of Object.keys(kitchen)) {
        if (kitchen[menuId]?.accessKitchenScreen === true) {
          navigate(`/mutfak-ekrani/${menuId}`);
          return;
        }
      }

      // 4) Müşteri ekranı (Pole Display)
      const pole = await getPermMap(uid, 'poleDisplayScreens');
      for (const menuId of Object.keys(pole)) {
        if (pole[menuId]?.accessPoleDisplay === true && pole[menuId]?.autoRedirect === true) {
          navigate(`/musteri-ekrani/${menuId}`);
          return;
        }
      }

      // 5) PYK ekranı (SQL ataması)
      if (await tryNavigateToSqlPykScreen(uid, navigate)) {
        return;
      }

      // 6) Hiçbir özel yetki yok → görev panosu
      navigate('/personnel/tasks');
      return;
    } catch (err) {
      console.error('[impersonate-redirect] İşçi yönlendirme hatası, görev panosuna düşülüyor:', err);
      navigate('/personnel/tasks');
      return;
    }
  }

  // Bilinmeyen/diğer roller → güvenli varsayılan
  navigate('/personnel/tasks');
}
